import { AxiosResponse } from "axios";
import { CHAT_ENDPOINT_QUERY_PARAMS } from "duck/constants";
import type { Message } from "duck/ui/chat/serverSentEvents/types";
import { Thread } from "duck/ui/chat/types";
import { mutate } from "swr";

import type { DeleteRequest } from "shared/api/api";
import { clientServerPydanticAI } from "shared/api/axios";
import { createURL } from "shared/api/utils";

// Request/Response types
export interface ListThreadsRequest {
  all_users?: boolean;
  user_filter?: string;
}

export interface ListThreadsResponse {
  threads: Thread[];
}

export interface CreateThreadRequest {
  thread_id?: string;
  title?: string;
  parent_thread_id?: string;
}

export interface ThreadTitleUpdate {
  uuid: string;
  title: string;
}

const THREADS_BASE_ROUTE = "threads";
const MESSAGES_ROUTE = "messages";

export const listThreadsRequestURI = (params: ListThreadsRequest): string =>
  clientServerPydanticAI.getUri({
    method: "GET",
    url: createURL([THREADS_BASE_ROUTE]),
    params,
  });

export interface ListThreadMessagesRequest {
  threadId: string;
  skipRequest?: boolean;
}

export interface ListThreadMessagesResponse {
  messages: Message[];
}

export interface ListThreadUsersResponse {
  users: string[];
}

export interface GetThreadRequest {
  threadId: string;
  skipRequest?: boolean;
}

export const getThreadRequestURI = (params: GetThreadRequest): string =>
  params.skipRequest
    ? ""
    : clientServerPydanticAI.getUri({
        method: "GET",
        url: createURL([THREADS_BASE_ROUTE, params.threadId]),
      });

export const listThreadMessagesRequestURI = (
  params: ListThreadMessagesRequest
): string =>
  params.skipRequest
    ? ""
    : clientServerPydanticAI.getUri({
        method: "GET",
        url: createURL([THREADS_BASE_ROUTE, params.threadId, MESSAGES_ROUTE]),
        params: CHAT_ENDPOINT_QUERY_PARAMS,
      });

const createThreadRequestURI = (): string =>
  clientServerPydanticAI.getUri({
    method: "POST",
    url: createURL([THREADS_BASE_ROUTE]),
  });

export const createThread = async (
  threadsRequestKey?: string,
  params?: CreateThreadRequest
): Promise<Thread> => {
  try {
    const response = await clientServerPydanticAI.post(
      createThreadRequestURI(),
      params
    );

    if (threadsRequestKey) {
      await mutate(threadsRequestKey);
    }

    return response.data;
  } catch (error) {
    console.error("Failed to create thread", error);
    throw error;
  }
};

const updateThreadTitleRequestURI = (thread_id: string): string =>
  clientServerPydanticAI.getUri({
    method: "POST",
    url: createURL([THREADS_BASE_ROUTE, thread_id, "update_title"]),
  });

export const updateThreadTitle = async (
  thread_id: string
): Promise<ThreadTitleUpdate> => {
  try {
    const response = await clientServerPydanticAI.post(
      updateThreadTitleRequestURI(thread_id)
    );

    return response.data;
  } catch (error) {
    console.error("Failed to update thread title", error);
    throw error;
  }
};

const deleteThreadRequestURI = (thread_id: string): string =>
  clientServerPydanticAI.getUri({
    method: "DELETE",
    url: createURL([THREADS_BASE_ROUTE, thread_id]),
  });

export const deleteThread = async (
  args: DeleteRequest
): Promise<AxiosResponse<any, any, {}>> =>
  clientServerPydanticAI.delete(deleteThreadRequestURI(args.id));

export const listThreadUsersRequestURI = (): string =>
  clientServerPydanticAI.getUri({
    method: "GET",
    url: createURL([THREADS_BASE_ROUTE, "users"]),
  });
